import { Container, Ticker } from "pixi.js";
import SignController from "../services/SignController";
import JackpotHistory from "../components/JackpotHistory";
import Background from "../components/Background";
import GlobalStore from "../services/GlobalStore";
import { CommonEvents, GameEvents, MessageEvents } from "../events/EventEnums";
import Scene from "../scenes/Scene";
import { ConfigParams } from "../GameConfig";
import { DEFAULT_JACKPOT_HIT } from "../StaticConfig";
import SceneCurrentJackpot from "../scenes/SceneCurrentJackpot";
import SceneJackpotHit from "../scenes/SceneJackpotHit";
import { JackpotHitData } from "../models/JackpotHitData";

enum State {
  CUR_JACKPOT,
  HISTORY,
  WIN,
}

export class DefaultSign {
  private jpQueue: JackpotHitData[] = [];
  private state: State = State.CUR_JACKPOT;

  private readonly width = 1920;
  private readonly height = 1080;

  private readonly controller: SignController;
  private readonly stage: Container;
  private readonly config: ConfigParams;

  private bg: Background;
  private history: JackpotHistory;

  private content: Container;

  private readonly layers = GlobalStore.getLayers();

  private sceneSwitchTimer: number | null = null;
  private winTimer: number | null = null;

  private curTimeMs: number = 0;
  private sceneSwitchMillis: number;
  private readonly sceneCurJackpot;
  private readonly sceneJackpotHit;

  constructor({
    stage,
    controller,
    config,
  }: {
    stage: Container;
    controller: SignController;
    config: ConfigParams;
  }) {
    this.config = config;
    this.stage = stage;
    this.controller = controller;

    // Initialize scene switch timing from configuration
    // Use current jackpot duration as the base scene switch time
    this.sceneSwitchMillis =
      GlobalStore.timings.currentJackpotScreenDurationSeconds * 1000;

    this.content = new Container();
    stage.addChild(this.content);
    this.layers.content.attach(this.content);

    this.bg = new Background();
    stage.addChild(this.bg);
    this.layers.background.attach(this.bg);

    this.history = new JackpotHistory(
      GlobalStore.generalSettings.showTimeInHistory,
      () => this.state !== State.WIN,
    );
    this.content.addChild(this.history);
    this.sceneCurJackpot = new SceneCurrentJackpot({
      parent: stage,
      config: config,
      name: "CurrentJackpot",
    });
    this.sceneJackpotHit = new SceneJackpotHit({
      parent: stage,
      name: "JackpotHit",
    });

    GlobalStore.commonEventEmitter.on(CommonEvents.UPDATED_CONFIG, () => {
      this.history.update(GlobalStore.getJackpotHistory());
      this.history.fadeIn();
    });
    GlobalStore.gameEventEmitter.on(GameEvents.JACKPOT_HISTORY_UPDATED, () => {
      this.history.update(GlobalStore.getJackpotHistory());
      this.history.fadeIn();
    });

    Ticker.shared.add(this.onTick);

    this.showCurJackpot();
    this.history.show();
    if (this.state === State.HISTORY) this.history.fadeIn();

    this.registerEventListeners();
  }

  private showHistory = () => {
    if (this.history.numEntries === 0) return;

    if (this.winTimer) clearTimeout(this.winTimer);
    this.history.show();
    this.sceneCurJackpot.hide();
    this.sceneJackpotHit.hide();
    const tl = this.history.expand(true);
    this.bg.toBottom();
    this.updateState(State.HISTORY);
    // tl?.then(() => {
    // });
  };

  private showCurJackpot = () => {
    if (this.winTimer) clearTimeout(this.winTimer);

    switch (this.state) {
      case State.HISTORY: {
        const tl = this.history.contract();
        if (tl) {
          tl.then(() => {
            if (this.state === State.WIN) return;
            this.sceneCurJackpot.fadeIn();
            this.updateState(State.CUR_JACKPOT);
          });
          this.bg.toTop();
        } else {
          this.history.hide();
          this.sceneCurJackpot.fadeIn();
          this.updateState(State.CUR_JACKPOT);
          this.bg.toTop();
        }
        break;
      }
      case State.WIN: {
        const tl = this.sceneJackpotHit.fadeOut();
        if (tl) {
          tl.then(() => {
            if (this.state !== State.WIN) return;
            this.sceneCurJackpot.fadeIn();
            this.updateState(State.CUR_JACKPOT);
          });
          this.bg.toTop();
        } else {
          this.sceneJackpotHit.hide();
          this.sceneCurJackpot.fadeIn();
          this.updateState(State.CUR_JACKPOT);
          this.bg.toTop();
        }
        break;
      }
      default:
        this.sceneCurJackpot.fadeIn();
        this.updateState(State.CUR_JACKPOT);
        this.bg.toTop();
        break;
    }
  };

  private jpHitLogic = () => {
    this.sceneJackpotHit.show();

    this.winTimer = setTimeout(() => {
      if (this.winTimer) clearTimeout(this.winTimer);
      this.winTimer = null;
      if (this.jpQueue.length > 0) {
        this.playWinScene();
      } else {
        this.showCurJackpot();
        this.history.fadeIn();
      }
    }, GlobalStore.timings.jackpotHitScreenDurationSeconds * 1000);
  };

  private playWinScene = (): boolean => {
    if (this.winTimer || this.jpQueue.length === 0) {
      return false;
    }

    if (this.sceneSwitchTimer) {
      clearTimeout(this.sceneSwitchTimer);
      this.sceneSwitchTimer = null;
    }

    GlobalStore.setJackpotHitData(this.jpQueue.shift() ?? null);

    switch (this.state) {
      case State.HISTORY: {
        const tl = this.history.contract();
        this.sceneCurJackpot.fadeOut();
        this.bg.toTop();
        if (tl) {
          tl.then(this.history.fadeOut).then(this.jpHitLogic);
        } else {
          this.history.fadeOut()?.then(this.jpHitLogic);
        }
        break;
      }
      case State.CUR_JACKPOT: {
        const tl = this.sceneCurJackpot.fadeOut();
        this.bg.toTop();
        this.history.fadeOut();
        if (tl) {
          tl.then(this.jpHitLogic);
        } else {
          this.jpHitLogic();
        }
        break;
      }
      case State.WIN: {
        this.jpHitLogic();
      }
    }

    this.updateState(State.WIN);
    return true;
  };

  private updateState = (state: State) => {
    this.state = state;
    this.curTimeMs = 0;
  };

  private onTick = (tick: Ticker) => {
    this.curTimeMs += tick.elapsedMS;

    // Use different switch times for different states
    const currentSwitchTime =
      this.state === State.CUR_JACKPOT
        ? this.sceneSwitchMillis
        : GlobalStore.timings.historyScreenDurationSeconds * 1000;

    if (this.curTimeMs > currentSwitchTime) {
      console.log(
        `Switching from ${State[this.state]} after ${currentSwitchTime / 1000} seconds`,
      );
      switch (this.state) {
        case State.CUR_JACKPOT:
          this.showHistory();
          break;
        case State.HISTORY:
          this.showCurJackpot();
          break;
        case State.WIN:
          break;
      }
      this.curTimeMs = 0;
    }
  };

  private registerEventListeners = (): void => {
    const ee = GlobalStore.gameEventEmitter;

    ee.on(GameEvents.JACKPOT_CUR_VALUES_UPDATED, this.onCurValueUpdate);
    ee.on(GameEvents.JACKPOT_HISTORY_UPDATED, this.onHistoryUpdated);
    ee.on(GameEvents.JACKPOT_HIT, this.onHit as any);
  };

  private onCurValueUpdate = () => {
    this.sceneCurJackpot.updateValues(GlobalStore.getCurJackpots());
  };

  private onHistoryUpdated = () => {
    this.history.update(GlobalStore.getJackpotHistory());
  };

  private onHit = (hit: JackpotHitData) => {
    this.jpQueue.push(hit);
    this.playWinScene();
  };
}
